import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import type { GarageSnapshotData, HomeNextAction } from './homeTypes';
import { normalizeNextActions } from './homeTypes';
import apiService from '../../services/ApiService';

interface GarageSnapshotProps {
  garage?: GarageSnapshotData | null;
  nextActions?: HomeNextAction[] | unknown[];
}

export const GarageSnapshot: React.FC<GarageSnapshotProps> = ({ garage, nextActions = [] }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const vehicles = garage?.vehicles || [];
  const isEmpty = vehicles.length === 0;
  const normalizedActions = normalizeNextActions(nextActions);
  const primaryAction = normalizedActions[0];

  return (
    <section className="py-10 sm:py-12" aria-labelledby="garage-snapshot-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <p className="kicker mb-2">Garage</p>
            <h2
              id="garage-snapshot-heading"
              className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white"
            >
              Your garage
            </h2>
            {!isEmpty ? (
              <p className="text-gray-400 text-sm mt-1.5">
                {vehicles.length} vehicle{vehicles.length === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>
          {!isEmpty ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/garage')}>
              Open garage
            </Button>
          ) : null}
        </div>

        {isEmpty ? (
          <div className="border border-dashed border-white/[0.1] rounded-xl" style={{ backgroundColor: 'rgba(12,12,12,0.6)' }}>
            <EmptyState
              variant="dense"
              imageSrc="/images/brand/empty-garage-bay.jpg"
              imageAlt=""
              title="Your garage is empty"
              description="Add a build once. Specs, mods, and photos stay with it."
              action={
                <>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      void apiService.trackEvent('home_cta_clicked', {
                        metadata: { cta: 'add_first_vehicle', surface: 'garage' },
                      });
                      navigate('/garage');
                    }}
                  >
                    Add a Build
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => navigate('/discover')}>
                    Browse builds
                  </Button>
                </>
              }
            />
          </div>
        ) : (
          <div className="space-y-4">
            <ul className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
              {vehicles.slice(0, 6).map((v) => {
                const img = resolvePublicFileUrl(v.image_url);
                const label = v.nickname || `${v.year} ${v.make} ${v.model}`;
                const slug = resolveVehicleNavSlug(v);
                const username = (v.owner_username || user?.username || '').trim();
                const profilePath =
                  username && slug ? vehicleProfilePath(username, slug) : '';
                return (
                  <li key={v.id} className="flex-shrink-0 w-36">
                    <button
                      type="button"
                      onClick={() => navigate(profilePath || '/garage')}
                      className="w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 rounded-lg"
                    >
                      <div className="aspect-[4/3] rounded-lg overflow-hidden bg-[#0a0a0a] mb-2">
                        {img ? (
                          <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                            No photo
                          </div>
                        )}
                      </div>
                      <p className="text-white text-sm font-medium truncate">{label}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
            {primaryAction ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 border border-white/[0.06] bg-[#0c0c0c]/50 rounded-xl px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium">{primaryAction.title}</p>
                  {primaryAction.body ? (
                    <p className="text-gray-400 text-xs mt-0.5">{primaryAction.body}</p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(primaryAction.href)}
                >
                  {primaryAction.cta_label}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

export default GarageSnapshot;
